import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, RouterLink } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { AgendaComponent } from '@shared/components/agenda/agenda.component';
import { Subject, catchError, combineLatest, forkJoin, map, of, startWith, switchMap } from 'rxjs';
import { UserFullInfoService } from '@core/services/user-full-info.service';
import { UnitService } from '../services/unit.service';
import { OrganizationLevel } from '../models/unit.model';
import { BirthdayMonth, groupBirthdays } from './unit-birthdays';

@Component({
  selector: 'app-unit-birthdays-page',
  standalone: true,
  imports: [CommonModule, RouterLink, MatIconModule, AgendaComponent],
  templateUrl: './unit-birthdays-page.component.html',
  styleUrl: './unit-birthdays-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UnitBirthdaysPageComponent {
  private readonly route = inject(ActivatedRoute);
  private readonly units = inject(UnitService);
  private readonly userInfo = inject(UserFullInfoService);
  private readonly reload$ = new Subject<void>();
  readonly vm$ = combineLatest([
    this.route.parent!.parent!.paramMap,
    this.userInfo.getUserInfo$(),
    this.reload$.pipe(startWith(void 0)),
  ]).pipe(switchMap(([params, user]) => {
    const unitId = params.get('unit-id')!;
    const canView = !!(user?.isAdmin || user?.readableUnitIds.some(id => id.toLowerCase() === unitId.toLowerCase()));
    const empty = { unitId, months: [] as BirthdayMonth[], showCommunity: false, loading: false, error: false, canView };
    if (!canView) return of(empty);
    return forkJoin({ members: this.units.getMembersFull(unitId), unit: this.units.getUnit(unitId) }).pipe(
      map(({ members, unit }) => ({
        ...empty,
        months: groupBirthdays(members, new Date()),
        showCommunity: unit.organizationLevelId < OrganizationLevel.Comunidad,
      })),
      startWith({ ...empty, loading: true }),
      catchError(() => of({ ...empty, error: true })),
    );
  }));

  retry(): void { this.reload$.next(); }

  phoneLink(phone?: string): string | null {
    const digits = phone?.replace(/\D/g, '');
    return digits ? `tel:${phone?.trim().startsWith('+') ? '+' : ''}${digits}` : null;
  }
}
